import type { ApiResponse, AuthTokens } from '@runninglime/shared';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

// ─── Token Management ────────────────────────────────────────────────────────

export function setTokens(tokens: { accessToken: string; refreshToken: string }) {
  accessToken = tokens.accessToken;
  localStorage.setItem('refreshToken', tokens.refreshToken);
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem('refreshToken');
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken');
}

// ─── Token Refresh ───────────────────────────────────────────────────────────

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      clearTokens();
      return false;
    }

    const data: ApiResponse<AuthTokens> = await res.json();
    if (data.success && data.data) {
      setTokens(data.data);
      return true;
    }

    clearTokens();
    return false;
  } catch {
    clearTokens();
    return false;
  }
}

// ─── API Client ──────────────────────────────────────────────────────────────

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T = unknown>(
  path: string,
  options: FetchOptions = {}
): Promise<ApiResponse<T>> {
  const { body, headers: extraHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // If 401 and we have a refresh token, try refresh once
  if (res.status === 401 && getRefreshToken()) {
    // Deduplicate concurrent refresh calls
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(`${API_BASE}${path}`, {
        ...rest,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
    }
  }

  const data: ApiResponse<T> = await res.json();
  return data;
}

// ─── Convenience Methods ─────────────────────────────────────────────────────

export const apiGet = <T>(path: string) => api<T>(path, { method: 'GET' });

export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body });

export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body });

export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body });

export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });

// ─── File Upload ─────────────────────────────────────────────────────────────

export async function apiUpload<T = unknown>(
  path: string,
  file: File
): Promise<ApiResponse<T>> {
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  // If 401 and we have a refresh token, try refresh once
  if (res.status === 401 && getRefreshToken()) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers,
        body: formData,
      });
    }
  }

  const data: ApiResponse<T> = await res.json();
  return data;
}
